import { Container, Link } from '@material-ui/core';
import useLanguageStore from '@stores/languageStore';
import { useTranslation } from 'react-i18next';
import SEOHead from '../../components/SEOHead';
import SectionHeader from '../../components/SectionHeader';

const AccessibilityPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';

  return (
    <div
      className={`min-h-[calc(100vh-64px)] py-20 sm:py-32 max-w-[100vw] overflow-x-hidden ${
        isRtl ? 'rtl' : 'ltr'
      }`}
    >
      <SEOHead title={t('accessibility.title')} />
      <Container maxWidth="lg" className="px-4 sm:px-8">
        <SectionHeader
          title={t('accessibility.title')}
          className="mb-12"
          titleWeight="800"
        />

        <div
          className={`leading-relaxed text-base mb-8 max-w-4xl mx-auto ${
            isRtl ? 'text-right' : 'text-left'
          }`}
        >
          {/* Commitment */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-[#063447]">
              {t('accessibility.commitment.title')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('accessibility.commitment.text')}
            </p>
          </section>

          {/* Standards */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-[#063447]">
              {t('accessibility.standards.title')}
            </h2>
            <p className="mb-4 text-gray-700">{t('accessibility.standards.text')}</p>
            <ul className="list-disc pr-6 mb-4 space-y-2 text-gray-700">
              <li>{t('accessibility.standards.item1')}</li>
              <li>{t('accessibility.standards.item2')}</li>
              <li>{t('accessibility.standards.item3')}</li>
              <li>{t('accessibility.standards.item4')}</li>
              <li>{t('accessibility.standards.item5')}</li>
            </ul>
          </section>

          {/* Features */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-[#063447]">
              {t('accessibility.features.title')}
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 list-disc pr-6 text-gray-700">
              <li>{t('accessibility.features.item1')}</li>
              <li>{t('accessibility.features.item2')}</li>
              <li>{t('accessibility.features.item3')}</li>
              <li>{t('accessibility.features.item4')}</li>
              <li>{t('accessibility.features.item5')}</li>
              <li>{t('accessibility.features.item6')}</li>
              <li>{t('accessibility.features.item7')}</li>
            </ul>
          </section>

          {/* Contact - Highlighted Box */}
          <section className="mb-12 bg-stone-50 p-8 rounded-xl border border-stone-100 shadow-sm">
            <h2 className="text-2xl font-bold mb-4 text-[#063447]">
              {t('accessibility.contact.title')}
            </h2>
            <p className="mb-6 text-gray-700">{t('accessibility.contact.text')}</p>
            <div className="space-y-3">
              <p className="text-lg">
                <strong className="font-semibold text-[#063447]">
                  {t('accessibility.contact.email')}:
                </strong>{' '}
                <Link
                  href="mailto:info@avnechen.co.il"
                  className="text-[#063447] hover:text-[#084e6b] underline decoration-2 underline-offset-4"
                >
                  info@avnechen.co.il
                </Link>
              </p>
              <p className="text-lg">
                <strong className="font-semibold text-[#063447]">
                  {t('accessibility.contact.phone')}:
                </strong>{' '}
                <Link
                  href="tel:+972505216139"
                  className="text-[#063447] hover:text-[#084e6b] underline decoration-2 underline-offset-4"
                >
                  {t('contact.info.phoneValue')}
                </Link>
              </p>
            </div>
          </section>

          {/* Improvements */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-[#063447]">
              {t('accessibility.improvements.title')}
            </h2>
            <p className="text-gray-700">{t('accessibility.improvements.text')}</p>
          </section>

          {/* Last Updated */}
          <section className="text-gray-500 text-sm border-t pt-6 mt-8">
            <p>
              {t('accessibility.lastUpdated.text')}{' '}
              {new Date().toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </section>
        </div>
      </Container>
    </div>
  );
};

export default AccessibilityPage;
